import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { metricsCategoryMeta } from '@utils/metrics/categoryMeta';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import {
  mergeObjectsWithSameKey,
  createActiveMetricKeys,
  createAllMetricKeys,
  createMetricsMap,
  createMetricsGroupedByCategory,
  createSecondaryOnlyMapping,
  createMetricsGroupedByType,
  getMetricsCategoryKeys,
  createFlowMetricsMeta,
  createFrustrationSignalMetricsMeta,
  createPercentileMetrics,
} from '@utils/metrics/utilities';

type State = {
  userDefinedMetricsMeta: any;
  preDefinedMetricsMeta: any;
  metricsMeta: any;
  activeMetricsKeys: any;
  flowConfigMeta: any;
  flowMetricsMeta: any;
  frustrationSignalMetricsMeta: any;
  frustrationSignalConfigMeta: any;
  eventConfigMeta: any;
  metricsGroupedByCategory: any;
  metricsGroupedByType: any;
  allMetricKeys: any;
  percentageMetricsMap: any;
  invertPercentageMetricsMap: any;
  primaryToPercentageLocalKeyMap: object;
  percentageLocalToPrimaryKeyMap: object;
  defaultSecondaryMetricsKeyMap: any;
  invertedDefaultSecondaryMetricsKeyMap: any;
  invertSecondaryOnlyMetricsKeyMap: any;
  secondaryOnlyMetricsKeyMap: any;
  metricsCategoryMeta: any;
  metricsCategoryKeys: any;
  percentileMetrics: any;
};

type Action = {
  mergePreDefinedMetricsMeta: (metricsMeta: any) => void;
  mergeUserDefinedMetricsMeta: (metricsMeta: any) => void;
  mergeCombinedMetricsMeta: (preDefinedMetricsMeta: any, userDefinedMetricsMeta: any) => void;
  setFlowConfigMeta: (flowConfigMeta: any) => void;
  setFlowMetricsMeta: (flowMetricsMeta: any) => void;
  setEventConfigMeta: (eventConfigMeta: any) => void;
  setFrustrationSignalMetricsMeta: (frustrationSignalMetricsMeta: any) => void;
  setFrustrationSignalConfigMeta: (frustrationSignalConfigMeta: any) => void;
  setMetricsCategoryMeta: (metricsCategoryMeta: any) => void;
};

const CURRENT_DEFAULT_CATEGORY_META = metricsCategoryMeta[CURRENT_PRODUCT_MODE.toLowerCase()];
const CURRENT_DEFAULT_CATEGORY_KEYS = getMetricsCategoryKeys(CURRENT_DEFAULT_CATEGORY_META);

const initialState: State = {
  userDefinedMetricsMeta: {},
  preDefinedMetricsMeta: {},
  flowConfigMeta: {},
  flowMetricsMeta: {},
  eventConfigMeta: {},
  frustrationSignalMetricsMeta: {},
  frustrationSignalConfigMeta: {},
  metricsMeta: {},
  metricsGroupedByCategory: {},
  metricsGroupedByType: {},
  activeMetricsKeys: {},
  allMetricKeys: {},
  percentageMetricsMap: {},
  invertPercentageMetricsMap: {},
  primaryToPercentageLocalKeyMap: {},
  percentageLocalToPrimaryKeyMap: {},
  defaultSecondaryMetricsKeyMap: {},
  invertedDefaultSecondaryMetricsKeyMap: {},
  invertSecondaryOnlyMetricsKeyMap: {},
  secondaryOnlyMetricsKeyMap: {},
  metricsCategoryMeta: CURRENT_DEFAULT_CATEGORY_META,
  metricsCategoryKeys: CURRENT_DEFAULT_CATEGORY_KEYS,
  percentileMetrics: {},
};

const commonMetricsUtility = (metricsMeta) => {
  const activeMetricsKeys = createActiveMetricKeys(metricsMeta);
  const allMetricKeys = createAllMetricKeys(metricsMeta);
  const {
    percentageMetricsMap,
    invertPercentageMetricsMap,
    primaryToPercentageLocalKeyMap,
    percentageLocalToPrimaryKeyMap,
    defaultSecondaryMetricsKeyMap,
    invertedDefaultSecondaryMetricsKeyMap,
  } = createMetricsMap(metricsMeta);
  const { secondaryOnlyMetricsKeyMap, invertSecondaryOnlyMetricsKeyMap } =
    createSecondaryOnlyMapping(metricsMeta, invertedDefaultSecondaryMetricsKeyMap);
  const metricsGroupedByCategory = createMetricsGroupedByCategory(metricsMeta);
  const metricsGroupedByType = createMetricsGroupedByType(metricsMeta);
  const percentileMetrics = createPercentileMetrics(metricsMeta);
  return {
    metricsMeta,
    activeMetricsKeys,
    allMetricKeys,
    percentageMetricsMap,
    invertPercentageMetricsMap,
    primaryToPercentageLocalKeyMap,
    percentageLocalToPrimaryKeyMap,
    defaultSecondaryMetricsKeyMap,
    invertedDefaultSecondaryMetricsKeyMap,
    secondaryOnlyMetricsKeyMap,
    invertSecondaryOnlyMetricsKeyMap,
    metricsGroupedByCategory,
    metricsGroupedByType,
    percentileMetrics,
  };
};

export const metricsStore = create<State & Action>()(
  devtools(
    (set: any, get: any) => ({
      ...initialState,
      mergePreDefinedMetricsMeta: (preDefinedMetricsMeta) =>
        set(() => {
          const metricsMeta = mergeObjectsWithSameKey(
            preDefinedMetricsMeta,
            get().userDefinedMetricsMeta,
          );
          const metricsUtility = commonMetricsUtility(metricsMeta);
          return {
            ...metricsUtility,
            preDefinedMetricsMeta,
          };
        }),
      mergeUserDefinedMetricsMeta: (userDefinedMetricsMeta) =>
        set(() => {
          const metricsMeta = mergeObjectsWithSameKey(
            get().preDefinedMetricsMeta,
            userDefinedMetricsMeta,
          );
          const metricsUtility = commonMetricsUtility(metricsMeta);
          return {
            ...metricsUtility,
            userDefinedMetricsMeta,
          };
        }),
      mergeCombinedMetricsMeta: (preDefinedMetricsMeta, userDefinedMetricsMeta) =>
        set(() => {
          const metricsMeta = mergeObjectsWithSameKey(
            preDefinedMetricsMeta,
            userDefinedMetricsMeta,
          );
          const metricsUtility = commonMetricsUtility(metricsMeta);
          return {
            ...metricsUtility,
            preDefinedMetricsMeta,
            userDefinedMetricsMeta,
          };
        }),
      setFlowConfigMeta: (flowConfigMeta) => set(() => ({ flowConfigMeta })),
      setFlowMetricsMeta: (flowConfigMeta) =>
        set(() => {
          return {
            flowMetricsMeta: createFlowMetricsMeta(get().metricsGroupedByCategory, flowConfigMeta),
          };
        }),
      setFrustrationSignalMetricsMeta: (frustrationSignalMetricsMeta) =>
        set(() => ({
          frustrationSignalMetricsMeta: createFrustrationSignalMetricsMeta(
            get().metricsGroupedByCategory,
            frustrationSignalMetricsMeta,
          ),
        })),
      setFrustrationSignalConfigMeta: (frustrationSignalConfigMeta) =>
        set(() => ({ frustrationSignalConfigMeta })),
      setEventConfigMeta: (eventConfigMeta) => set(() => ({ eventConfigMeta })),
      setMetricsCategoryMeta: (metricsCategoryMetaName: string = CURRENT_PRODUCT_MODE) => {
        const selectedCategoryMeta = metricsCategoryMeta[metricsCategoryMetaName.toLowerCase()];

        return set(() => ({
          metricsCategoryMeta: selectedCategoryMeta,
          metricsCategoryKeys: getMetricsCategoryKeys(selectedCategoryMeta),
        }));
      },
    }),
    { name: 'metricStore' },
  ),
);

export default metricsStore;
